import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  addRemoteEncouragement,
  createRemoteCommunityPost,
  deleteRemoteCommunityPost,
  fetchCommunityFeed,
  isCommunityApiConfigured,
  joinCommunityRemote,
  reportRemoteCommunityPost,
} from "../services/communityApi.js";

const PROFILE_KEY = "community_profile_v1";
const POSTS_KEY = "community_posts_v1";

/** @typedef {'new_habit'|'article_book'|'system_built'|'evolution'|'app_helped'|'environment'|'experience'|'encouragement'} CommunityCategory */

/**
 * @typedef {Object} CommunityEncouragement
 * @property {string} id
 * @property {string} userId
 * @property {string} text
 * @property {string} createdAt
 */

/**
 * @typedef {Object} CommunityPost
 * @property {string} id
 * @property {string} userId
 * @property {CommunityCategory} category
 * @property {string} body
 * @property {string} createdAt
 * @property {'local'|'remote'} [source]
 * @property {CommunityEncouragement[]} [encouragements]
 */

/**
 * @typedef {Object} CommunityProfile
 * @property {string} userId
 * @property {string} joinedAt
 * @property {string} [token]
 */

export const COMMUNITY_CATEGORIES = [
  { id: "new_habit", label: "New habit", emoji: "🌱" },
  { id: "article_book", label: "Article or book", emoji: "📚" },
  { id: "system_built", label: "System I built", emoji: "⚙️" },
  { id: "evolution", label: "How I've evolved", emoji: "📈" },
  { id: "app_helped", label: "How the app helped", emoji: "💡" },
  { id: "environment", label: "Environment redesign", emoji: "🏠" },
  { id: "experience", label: "Overall experience", emoji: "✨" },
  { id: "encouragement", label: "Encourage others", emoji: "💬" },
];

export { isCommunityApiConfigured };

/**
 * @param {string} raw
 */
export function normalizeCommunityUserId(raw) {
  return String(raw || "")
    .trim()
    .replace(/^@+/, "");
}

/**
 * Pseudonym rules — not a real name; letters, numbers, underscore.
 * @param {string} userId
 * @returns {{ ok: boolean, error?: string }}
 */
export function validateCommunityUserId(userId) {
  const id = normalizeCommunityUserId(userId);
  if (id.length < 3) return { ok: false, error: "User ID must be at least 3 characters." };
  if (id.length > 24) return { ok: false, error: "User ID must be 24 characters or fewer." };
  if (/\s/.test(id)) return { ok: false, error: "No spaces — pick a nickname, not your real name." };
  if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(id)) {
    return { ok: false, error: "Use letters, numbers, and underscores only. Start with a letter." };
  }
  const lower = id.toLowerCase();
  const banned = ["admin", "moderator", "procsystim", "support", "official"];
  if (banned.some((b) => lower === b || lower.includes(b))) {
    return { ok: false, error: "Choose a more unique nickname." };
  }
  return { ok: true };
}

/**
 * @returns {Promise<CommunityProfile|null>}
 */
export async function loadCommunityProfile() {
  try {
    const raw = await AsyncStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.userId) return null;
    return {
      userId: parsed.userId,
      joinedAt: parsed.joinedAt || new Date().toISOString(),
      token: parsed.token || "",
    };
  } catch {
    return null;
  }
}

/**
 * Join community (live: unique nickname + token; local: device-only profile).
 * @param {string} userId
 */
export async function saveCommunityProfile(userId) {
  const check = validateCommunityUserId(userId);
  if (!check.ok) throw new Error(check.error || "Invalid user ID");

  const normalized = normalizeCommunityUserId(userId);

  if (isCommunityApiConfigured()) {
    const remote = await joinCommunityRemote(normalized);
    if (!remote?.token) throw new Error("Server did not return a community token.");
    const profile = {
      userId: remote.userId || normalized,
      joinedAt: remote.joinedAt || new Date().toISOString(),
      token: remote.token,
    };
    await AsyncStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    return profile;
  }

  const profile = { userId: normalized, joinedAt: new Date().toISOString(), token: "" };
  await AsyncStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  return profile;
}

async function requireLiveToken() {
  const profile = await loadCommunityProfile();
  if (!profile?.token) {
    throw new Error("Rejoin the community to get a secure session token.");
  }
  return profile;
}

/**
 * @returns {Promise<CommunityPost[]>}
 */
export async function loadLocalCommunityPosts() {
  try {
    const raw = await AsyncStorage.getItem(POSTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * @param {CommunityPost[]} posts
 */
async function saveLocalCommunityPosts(posts) {
  await AsyncStorage.setItem(POSTS_KEY, JSON.stringify(posts));
}

/**
 * @param {CommunityCategory} category
 * @param {string} body
 * @param {string} userId
 */
export async function createCommunityPost(category, body, userId) {
  const text = body.trim();
  if (text.length < 8) throw new Error("Write at least a few words so others can learn from your story.");
  if (text.length > 2000) throw new Error("Keep your post under 2000 characters.");

  const normalized = normalizeCommunityUserId(userId);

  if (isCommunityApiConfigured()) {
    const profile = await requireLiveToken();
    return createRemoteCommunityPost(profile.token, category, text);
  }

  const post = {
    id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    userId: normalized,
    category,
    body: text,
    createdAt: new Date().toISOString(),
    source: "local",
    encouragements: [],
  };

  const existing = await loadLocalCommunityPosts();
  await saveLocalCommunityPosts([post, ...existing]);
  return post;
}

/**
 * @param {string} postId
 * @param {string} userId
 * @param {string} text
 */
export async function addEncouragement(postId, userId, text) {
  const msg = text.trim();
  if (msg.length < 2) throw new Error("Write a short encouraging message.");
  if (msg.length > 280) throw new Error("Keep encouragement under 280 characters.");

  const normalized = normalizeCommunityUserId(userId);

  if (isCommunityApiConfigured()) {
    const profile = await requireLiveToken();
    const result = await addRemoteEncouragement(postId, profile.token, msg);
    if (!result?.post) throw new Error("Post not found.");
    return { post: result.post, encouragement: result.encouragement, scope: "remote" };
  }

  const encouragement = {
    id: `enc_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    userId: normalized,
    text: msg,
    createdAt: new Date().toISOString(),
  };

  const locals = await loadLocalCommunityPosts();
  const idx = locals.findIndex((p) => p.id === postId);
  if (idx >= 0) {
    const copy = [...locals];
    const post = { ...copy[idx], encouragements: [...(copy[idx].encouragements || []), encouragement] };
    copy[idx] = post;
    await saveLocalCommunityPosts(copy);
    return { post, encouragement, scope: "local" };
  }

  throw new Error("Post not found.");
}

/**
 * @param {string} postId
 * @param {string} userId
 */
export async function deleteCommunityPost(postId, userId) {
  if (isCommunityApiConfigured()) {
    const profile = await requireLiveToken();
    await deleteRemoteCommunityPost(postId, profile.token);
    return;
  }

  const normalized = normalizeCommunityUserId(userId);
  const locals = await loadLocalCommunityPosts();
  const next = locals.filter((p) => !(p.id === postId && p.userId === normalized));
  if (next.length === locals.length) throw new Error("Post not found or you are not the author.");
  await saveLocalCommunityPosts(next);
}

/**
 * @param {string} postId
 * @param {string} [reason]
 */
export async function reportCommunityPost(postId, reason = "") {
  if (!isCommunityApiConfigured()) {
    throw new Error("Reporting requires the live community.");
  }
  const profile = await requireLiveToken();
  return reportRemoteCommunityPost(postId, profile.token, reason);
}

async function loadOfflineCommunityFeed() {
  const local = await loadLocalCommunityPosts();
  local.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return local;
}

/**
 * @returns {Promise<CommunityPost[]>}
 */
export async function loadCommunityFeed() {
  if (isCommunityApiConfigured()) {
    return fetchCommunityFeed();
  }
  return loadOfflineCommunityFeed();
}

/**
 * @param {CommunityCategory} category
 */
export function getCategoryMeta(category) {
  return COMMUNITY_CATEGORIES.find((c) => c.id === category) || COMMUNITY_CATEGORIES[7];
}
